/**
 * Performance Optimization Initialization
 * 
 * Sets up caching, monitoring, and optimization strategies
 * for the production API migration
 */

import { cacheManager, CacheConfig } from './cache-manager';
import { performanceOptimizer, OptimizationStrategy } from './performance-optimizer';
import { enhancedApiClient } from './enhanced-client';
import { performanceUtils } from './optimized-services';

type PerformanceSummary = ReturnType<typeof performanceUtils.getPerformanceSummary>;

export interface PerformanceInitConfig {
  cache: Partial<CacheConfig>;
  monitoring: {
    enabled: boolean;
    alertThreshold: number;
    reportingInterval: number;
  };
  optimization: {
    enabled: boolean;
    autoCache: boolean;
    prefetch: boolean;
  };
  endpoints: {
    [key: string]: {
      cacheStrategy: 'aggressive' | 'moderate' | 'minimal' | 'disabled';
      maxCacheTime: number;
      priority: 'low' | 'medium' | 'high' | 'critical';
    };
  };
}

const DEFAULT_CONFIG: PerformanceInitConfig = {
  cache: {
    enabled: true,
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    maxMemoryEntries: 1000,
    maxStorageSize: 10 * 1024 * 1024, // 10MB
    storage: 'hybrid',
    compressionEnabled: true,
    metrics: true,
  },
  monitoring: {
    enabled: true,
    alertThreshold: 2000, // 2 seconds
    reportingInterval: 60000, // 1 minute
  },
  optimization: {
    enabled: true,
    autoCache: true,
    prefetch: true,
  },
  endpoints: {
    '/establishments': {
      cacheStrategy: 'aggressive',
      maxCacheTime: 30 * 60 * 1000, // 30 minutes
      priority: 'high',
    },
    '/users': {
      cacheStrategy: 'moderate',
      maxCacheTime: 10 * 60 * 1000, // 10 minutes
      priority: 'medium',
    },
    '/preventive-maintenances': {
      cacheStrategy: 'moderate',
      maxCacheTime: 5 * 60 * 1000, // 5 minutes
      priority: 'medium',
    },
    '/communications': {
      cacheStrategy: 'minimal',
      maxCacheTime: 2 * 60 * 1000, // 2 minutes
      priority: 'low',
    },
    '/documents': {
      cacheStrategy: 'moderate',
      maxCacheTime: 15 * 60 * 1000, // 15 minutes
      priority: 'medium',
    },
    '/notifications': {
      cacheStrategy: 'minimal',
      maxCacheTime: 30 * 1000, // 30 seconds
      priority: 'low',
    },
  },
};

export class PerformanceInitializer {
  private config: PerformanceInitConfig;
  private initialized = false;
  private monitoringInterval?: NodeJS.Timeout;

  constructor(config: Partial<PerformanceInitConfig> = {}) {
    this.config = this.mergeConfig(DEFAULT_CONFIG, config);
  }

  /**
   * Initialize the performance optimization system
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.warn('Performance optimizer already initialized');
      return;
    }

    try {
      console.log('Initializing performance optimization system...');

      // Configure cache manager
      cacheManager.configure(this.config.cache);

      // Configure enhanced API client
      enhancedApiClient.configure({
        enableGlobalCaching: this.config.cache.enabled,
        enableGlobalMetrics: this.config.monitoring.enabled,
        enableGlobalOptimization: this.config.optimization.enabled,
        defaultCacheTTL: this.config.cache.defaultTTL || 5 * 60 * 1000,
      });

      // Set up optimization strategies
      this.setupOptimizationStrategies();

      // Set up monitoring
      if (this.config.monitoring.enabled) {
        this.setupMonitoring();
      }

      // Prefetch critical data
      if (this.config.optimization.prefetch) {
        await this.prefetchCriticalData();
      }

      // Set up cleanup intervals
      this.setupCleanupTasks();

      this.initialized = true;
      console.log('Performance optimization system initialized successfully');

      // Report initial status
      this.reportInitializationStatus();
    } catch (error) {
      console.error('Failed to initialize performance optimization:', error);
      throw error;
    }
  }

  /**
   * Shutdown the performance optimization system
   */
  shutdown(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    console.log('Performance optimization system shutdown');
    this.initialized = false;
  }

  /**
   * Update configuration at runtime
   */
  updateConfig(newConfig: Partial<PerformanceInitConfig>): void {
    this.config = this.mergeConfig(this.config, newConfig);
    
    // Re-configure components
    cacheManager.configure(this.config.cache);
    enhancedApiClient.configure({
      enableGlobalCaching: this.config.cache.enabled,
      enableGlobalMetrics: this.config.monitoring.enabled,
      enableGlobalOptimization: this.config.optimization.enabled,
      defaultCacheTTL: this.config.cache.defaultTTL || 5 * 60 * 1000,
    });

    console.log('Performance configuration updated');
  }

  /**
   * Get current performance status
   */
  getStatus(): {
    initialized: boolean;
    config: PerformanceInitConfig;
    metrics: PerformanceSummary | null;
    recommendations: string[];
  } {
    return {
      initialized: this.initialized,
      config: this.config,
      metrics: this.initialized ? performanceUtils.getPerformanceSummary() : null,
      recommendations: this.generateConfigRecommendations(),
    };
  }

  // Private methods

  private mergeConfig(
    base: PerformanceInitConfig,
    override: Partial<PerformanceInitConfig>
  ): PerformanceInitConfig {
    return {
      cache: { ...base.cache, ...override.cache },
      monitoring: { ...base.monitoring, ...override.monitoring },
      optimization: { ...base.optimization, ...override.optimization },
      endpoints: { ...base.endpoints, ...override.endpoints },
    };
  }

  private setupOptimizationStrategies(): void {
    // Auto-caching strategy for slow endpoints
    const autoCacheStrategy: OptimizationStrategy = {
      name: 'auto-cache-production-endpoints',
      condition: (metrics) => 
        metrics.avgResponseTime > 1000 && 
        metrics.requestCount > 5 && 
        metrics.errorRate < 0.1,
      action: async (endpoint, _metrics) => {
        console.log(`Auto-enabling cache for slow production endpoint: ${endpoint}`);
        
        // Find endpoint configuration
        const endpointConfig = Object.entries(this.config.endpoints).find(
          ([pattern]) => endpoint.includes(pattern)
        );

        if (endpointConfig) {
          const [, config] = endpointConfig;
          // Could implement dynamic cache configuration here
          console.log(`Recommended cache strategy: ${config.cacheStrategy}`);
        }
      },
    };

    // Production API error monitoring
    const errorMonitoringStrategy: OptimizationStrategy = {
      name: 'production-api-error-monitoring',
      condition: (metrics) => metrics.errorRate > 0.05 && metrics.requestCount > 10,
      action: async (endpoint, metrics) => {
        console.error(`High error rate detected for production endpoint: ${endpoint}`, {
          errorRate: `${(metrics.errorRate * 100).toFixed(1)}%`,
          requestCount: metrics.requestCount,
          avgResponseTime: `${Math.round(metrics.avgResponseTime)}ms`,
        });

        // Could emit to error tracking service
        this.emitErrorAlert(endpoint, metrics);
      },
    };

    // Performance degradation detection
    const performanceDegradationStrategy: OptimizationStrategy = {
      name: 'performance-degradation-detection',
      condition: (metrics) => 
        metrics.avgResponseTime > 3000 || 
        (metrics.maxResponseTime > 10000 && metrics.requestCount > 5),
      action: async (endpoint, metrics) => {
        console.warn(`Performance degradation detected: ${endpoint}`, {
          avgResponseTime: `${Math.round(metrics.avgResponseTime)}ms`,
          maxResponseTime: `${Math.round(metrics.maxResponseTime)}ms`,
          suggestions: this.generatePerformanceSuggestions(endpoint, metrics),
        });
      },
    };

    // Add strategies to optimizer
    performanceOptimizer.addOptimizationStrategy(autoCacheStrategy);
    performanceOptimizer.addOptimizationStrategy(errorMonitoringStrategy);
    performanceOptimizer.addOptimizationStrategy(performanceDegradationStrategy);
  }

  private setupMonitoring(): void {
    this.monitoringInterval = setInterval(() => {
      const summary = performanceUtils.getPerformanceSummary();
      
      // Check for critical issues
      this.checkCriticalIssues(summary);
      
      // Log performance summary periodically
      if (summary.summary.totalRequests > 0) {
        console.log('Performance Summary:', {
          avgResponseTime: `${Math.round(summary.summary.avgResponseTime)}ms`,
          cacheHitRate: `${Math.round(summary.cache.hitRate * 100)}%`,
          slowEndpoints: summary.summary.slowEndpoints,
          totalRequests: summary.summary.totalRequests,
        });
      }
    }, this.config.monitoring.reportingInterval);
  }

  private async prefetchCriticalData(): Promise<void> {
    try {
      console.log('Prefetching critical data...');
      await performanceUtils.prefetchCommonData();
      console.log('Critical data prefetched successfully');
    } catch (error) {
      console.warn('Failed to prefetch critical data:', error);
    }
  }

  private setupCleanupTasks(): void {
    // Cache cleanup every 10 minutes
    setInterval(() => {
      const stats = cacheManager.getMetrics();
      if (stats.entryCount > 800) {
        console.log('Running cache cleanup...');
        // Cache cleanup is handled internally by the cache manager
      }
    }, 10 * 60 * 1000);

    // Metrics cleanup daily
    setInterval(() => {
      console.log('Running daily metrics cleanup...');
      // Cleanup is handled by performance optimizer
    }, 24 * 60 * 60 * 1000);
  }

  private checkCriticalIssues(summary: PerformanceSummary): void {
    const metrics = summary.summary;
    const cache = summary.cache;

    // Critical response time
    if (metrics.avgResponseTime > 5000) {
      console.error('CRITICAL: Average response time exceeds 5 seconds', {
        avgResponseTime: `${Math.round(metrics.avgResponseTime)}ms`,
        slowEndpoints: metrics.slowEndpoints,
      });
    }

    // Critical cache performance
    if (cache.hitRate < 0.2 && metrics.totalRequests > 50) {
      console.error('CRITICAL: Cache hit rate is critically low', {
        hitRate: `${Math.round(cache.hitRate * 100)}%`,
        totalRequests: metrics.totalRequests,
      });
    }

    // High error rate
    if (metrics.highErrorRateEndpoints > 3) {
      console.error('CRITICAL: Multiple endpoints with high error rates', {
        count: metrics.highErrorRateEndpoints,
      });
    }
  }

  private emitErrorAlert(endpoint: string, metrics: { errorRate: number; requestCount: number; avgResponseTime: number }): void {
    // Emit to external monitoring service
    const alertData = {
      type: 'high_error_rate',
      endpoint,
      metrics: {
        errorRate: metrics.errorRate,
        requestCount: metrics.requestCount,
        avgResponseTime: metrics.avgResponseTime,
      },
      timestamp: Date.now(),
      severity: metrics.errorRate > 0.2 ? 'critical' : 'warning',
    };

    // Could send to external service like Sentry, DataDog, etc.
    console.error('Performance Alert:', alertData);
  }

  private generatePerformanceSuggestions(endpoint: string, metrics: {
    avgResponseTime: number;
    errorRate: number;
    requestCount: number;
    cacheHitRate?: number;
  }): string[] {
    const suggestions: string[] = [];

    if (metrics.avgResponseTime > 2000) {
      suggestions.push('Consider implementing response caching');
      suggestions.push('Review database query performance');
    }

    if (endpoint.includes('/search')) {
      suggestions.push('Implement search result caching');
      suggestions.push('Use request debouncing for search queries');
    }

    if (endpoint.includes('/list') || endpoint.includes('/index')) {
      suggestions.push('Implement pagination optimization');
      suggestions.push('Consider using virtual scrolling for large datasets');
    }

    if ((metrics.cacheHitRate ?? 0) < 0.3) {
      suggestions.push('Improve caching strategy for this endpoint');
    }

    return suggestions;
  }

  private generateConfigRecommendations(): string[] {
    const recommendations: string[] = [];

    if (!this.config.cache.enabled) {
      recommendations.push('Enable caching to improve performance');
    }

    if (!this.config.monitoring.enabled) {
      recommendations.push('Enable monitoring to track performance metrics');
    }

    if (!this.config.optimization.prefetch) {
      recommendations.push('Enable prefetching for better user experience');
    }

    if (this.config.cache.defaultTTL && this.config.cache.defaultTTL < 60000) {
      recommendations.push('Consider increasing default cache TTL for better hit rates');
    }

    return recommendations;
  }

  private reportInitializationStatus(): void {
    const status = this.getStatus();
    console.log('Performance System Status:', {
      initialized: status.initialized,
      cacheEnabled: this.config.cache.enabled,
      monitoringEnabled: this.config.monitoring.enabled,
      optimizationEnabled: this.config.optimization.enabled,
      endpointConfigs: Object.keys(this.config.endpoints).length,
    });

    if (status.recommendations.length > 0) {
      console.log('Initialization Recommendations:', status.recommendations);
    }
  }
}

// Create and export singleton instance
export const performanceInitializer = new PerformanceInitializer();

// Initialize on module load
export const initializePerformanceOptimization = async (
  config?: Partial<PerformanceInitConfig>
): Promise<void> => {
  if (config) {
    performanceInitializer.updateConfig(config);
  }
  
  await performanceInitializer.initialize();
};

// Export for use in app initialization
export const setupPerformanceOptimization = async (): Promise<void> => {
  try {
    await initializePerformanceOptimization();
    
    // Set up error boundary for performance optimization
    window.addEventListener('unload', () => {
      performanceInitializer.shutdown();
    });

    // Set up development tools
    if (import.meta.env.DEV) {
      // Make performance tools available in console
      (window as typeof window & { performanceTools: Record<string, unknown> }).performanceTools = {
        getStatus: () => performanceInitializer.getStatus(),
        getMetrics: () => performanceUtils.getPerformanceSummary(),
        clearCache: () => performanceUtils.clearAllCaches(),
        cacheManager,
        performanceOptimizer,
      };
      
      console.log('Performance tools available at window.performanceTools');
    }
  } catch (error) {
    console.error('Failed to setup performance optimization:', error);
    // Don't throw - app should still work without optimization
  }
};

export default performanceInitializer;
